import { useState, useCallback } from "react";
import i18n from "i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserSync } from "./useUserSync.js";
import { toast } from "react-toastify";
import { useApiWithAuth } from "./useAuth.js";
import { showSuccessToast } from "../shared/appToast.js";
import { loginWithReturn } from "../lib/loginWithReturn.js";
import { promptForPushPermission } from "../lib/push/pushClient.js";
interface WishlistItem {
  animalId: string;
  animalType: "cao" | "gato" | "ninhada";
  addedAt?: string;
}

interface UseWishlistReturn {
  wishlistItems: Set<string>;
  isInWishlist: (
    animalId: string,
    animalType: "cao" | "gato" | "ninhada",
  ) => boolean;
  addToWishlist: (
    animalId: string,
    animalType: "cao" | "gato" | "ninhada",
    animalName?: string,
  ) => Promise<{ success: boolean; likes?: number; wishlistItem?: any }>;
  removeFromWishlist: (
    animalId: string,
    animalType: "cao" | "gato" | "ninhada",
    animalName?: string,
  ) => Promise<{ success: boolean; likes?: number }>;
  toggleWishlist: (
    animalId: string,
    animalType: "cao" | "gato" | "ninhada",
    animalName?: string,
  ) => Promise<{ success: boolean; likes?: number; wishlistItem?: any }>;
  loading: boolean;
  fetchUserWishlist: () => Promise<void>;
  clearWishlist: () => void;
}

export const useWishlist = (): UseWishlistReturn => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { apiCall } = useApiWithAuth();
  const { user: dbUser } = useUserSync();
  const [wishlistItems, setWishlistItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Helper to convert frontend animal type to backend format
  const convertAnimalType = (
    type: "cao" | "gato" | "ninhada",
  ): "cao" | "gato" | "ninhada" => {
    return type;
  };

  // Helper to create unique key for wishlist items
  const createWishlistKey = (
    animalId: string,
    animalType: "cao" | "gato" | "ninhada",
  ): string => {
    return `${animalId}-${convertAnimalType(animalType)}`;
  };

  // Fetch all user's wishlist items and populate the Set
  const fetchUserWishlist = useCallback(async () => {
    // console.log("fetchUserWishlist called", {
    //   isAuthenticated,
    //   userId: dbUser?.id,
    // });
    if (!isAuthenticated || !dbUser?.id) return;

    try {
      setLoading(true);
      const response = await apiCall(
        `/api/wishlist/user/${dbUser.id}/animals?page=1&limit=100`,
      );
      if (!response.ok) {
        let apiMessage = "";
        try {
          const payload = await response.json();
          apiMessage = payload?.message || payload?.error || "";
        } catch {
          // Ignore JSON parse errors from non-JSON responses
        }
        throw new Error(
          apiMessage || `Failed to fetch wishlist (status ${response.status})`,
        );
      }
      const data = await response.json();
      // console.log("Wishlist data received:", data);

      if (data.success) {
        const wishlistSet = new Set<string>();
        if (Array.isArray(data.animals)) {
          data.animals.forEach(
            (animal: { id: string; type: "cao" | "gato" | "ninhada" }) => {
              const key = createWishlistKey(animal.id, animal.type);
              wishlistSet.add(key);
            },
          );
        } else if (Array.isArray(data.wishlist)) {
          // Backward compatibility with old wishlist payload shape.
          data.wishlist.forEach((item: WishlistItem) => {
            const frontendType = item.animalType;
            const key = createWishlistKey(item.animalId, frontendType);
            wishlistSet.add(key);
          });
        }
        // console.log(
        //   "Wishlist Set populated with",
        //   wishlistSet.size,
        //   "items:",
        //   Array.from(wishlistSet),
        // );
        setWishlistItems(wishlistSet);
      }
    } catch (error) {
      console.error("Error fetching user wishlist:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, dbUser?.id, apiCall]);

  // Check if animal is in wishlist
  const isInWishlist = useCallback(
    (animalId: string, animalType: "cao" | "gato" | "ninhada"): boolean => {
      const key = createWishlistKey(animalId, animalType);
      return wishlistItems.has(key);
    },
    [wishlistItems],
  );

  const clearWishlist = useCallback(() => {
    setWishlistItems(new Set());
  }, []);

  // Add animal to wishlist
  const addToWishlist = useCallback(
    async (
      animalId: string,
      animalType: "cao" | "gato" | "ninhada",
      animalName?: string,
    ) => {
      if (!isAuthenticated) {
        toast.info(i18n.t("utils:wishlist.loginRequired"));
        setTimeout(() => {
          loginWithReturn(loginWithRedirect);
        }, 1000);
        return { success: false };
      }

      if (!dbUser?.id) {
        toast.error(i18n.t("errors:wishlist.userNotSynced"));
        return { success: false };
      }

      try {
        setLoading(true);
        const response = await apiCall("/api/wishlist", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: dbUser.id,
            animalId,
            animalType: convertAnimalType(animalType),
          }),
        });

        const data = await response.json();

        if (data.success) {
          const key = createWishlistKey(animalId, animalType);
          setWishlistItems((prev) => new Set([...prev, key]));

          const displayName = animalName || i18n.t("utils:wishlist.fallbackName");
          showSuccessToast(i18n.t("utils:wishlist.added", { name: displayName }));

          // Contextual push opt-in: after the user's first like, ask for OS
          // notification permission (no-op on web / after the first prompt).
          void promptForPushPermission(apiCall);

          return {
            success: true,
            likes: data.likes ?? undefined,
            wishlistItem: data.wishlistItem,
          };
        } else {
          if (response.status === 409) {
            toast.info(i18n.t("utils:wishlist.alreadyAdded"));
            return { success: false };
          } else {
            throw new Error(data.message || "Failed to add to wishlist");
          }
        }
      } catch (error) {
        console.error("Error adding to wishlist:", error);
        toast.error(i18n.t("errors:wishlist.addFailed"));
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, dbUser?.id, loginWithRedirect, apiCall],
  );

  // Remove animal from wishlist
  const removeFromWishlist = useCallback(
    async (
      animalId: string,
      animalType: "cao" | "gato" | "ninhada",
      animalName?: string,
    ) => {
      if (!isAuthenticated || !dbUser?.id) return { success: false };

      try {
        setLoading(true);
        const backendType = convertAnimalType(animalType);
        const response = await apiCall(
          `/api/wishlist/${dbUser.id}/${animalId}?animalType=${backendType}`,
          {
            method: "DELETE",
          },
        );

        const data = await response.json();

        if (data.success) {
          const key = createWishlistKey(animalId, animalType);
          setWishlistItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
          });

          const displayName = animalName || i18n.t("utils:wishlist.fallbackName");
          showSuccessToast(i18n.t("utils:wishlist.removed", { name: displayName }));

          return { success: true, likes: data.likes ?? undefined };
        } else {
          throw new Error(data.message || "Failed to remove from wishlist");
        }
      } catch (error) {
        console.error("Error removing from wishlist:", error);
        toast.error(i18n.t("errors:wishlist.removeFailed"));
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, dbUser?.id, apiCall],
  );

  // Toggle wishlist status
  const toggleWishlist = useCallback(
    async (
      animalId: string,
      animalType: "cao" | "gato" | "ninhada",
      animalName?: string,
    ) => {
      if (isInWishlist(animalId, animalType)) {
        return await removeFromWishlist(animalId, animalType, animalName);
      } else {
        return await addToWishlist(animalId, animalType, animalName);
      }
    },
    [isInWishlist, addToWishlist, removeFromWishlist],
  );

  return {
    wishlistItems,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    loading,
    fetchUserWishlist,
    clearWishlist,
  };
};
